<template>
  <toolbar-container>
    <template #toolbar>
      <newsletter-quota-bar />
      <main-toolbar>
        <date-range-picker-field
          :value="filterDateRange"
          content-style="min-width: 200px; max-width: 200px"
          content-class="mt-2 pr-4"
          :label="$t('label.date_range')"
          disable-future
          @input="filterDateRangeSelected"
        />
        <v-spacer />
        <v-checkbox
          v-model="showArchived"
          :label="$t('button.show_archived')"
          dense
          class="mr-4"
          hide-details
          @change="changeShowArchived"
        />
        <v-btn
          color="success"
          @click="newCampaign"
        >
          {{ $t('button.create_new_campaign') }}
        </v-btn>
      </main-toolbar>
    </template>

    <newsletter-campaign-detail-dialog
      v-if="selectedMarketingCampaign"
      :campaign="selectedMarketingCampaign"
      @close="selectedMarketingCampaign=undefined"
    />
    <v-data-table
      class="clickable-table"
      dense
      :items-per-page="50"
      :headers="headers"
      :items="campaigns"
      :footer-props="{ itemsPerPageOptions: [25, 50, 100, 200, -1] }"
      @click:row="showCampaignDetail"
    >
      <template #no-data>
        <p class="py-16 my-16">
          {{ $t('message.no_newsletter_campaigns') }}
        </p>
      </template>
      <template #item.date="{item}">
        {{ localized.numericDateTimeText(item.date) }}
      </template>

      <template #item.recipientCount="{item}">
        {{ item.recipientCount ?? '-' }}
      </template>

      <template #item.status="{item}">
        <v-chip
          v-if="item.isArchived"
          color="white"
          class="black--text mr-2"
          x-small
          label
        >
          <v-icon
            x-small
            class="mr-1"
          >
            mdi-archive
          </v-icon>
          {{ $t('code.newsletter_campaign_status.archived') }}
        </v-chip>
        <v-chip
          v-if="item.status !== MarketingCampaignStatus.None"
          :color="colorForCampaignStatus(item.status)"
          class="black--text"
          x-small
          label
        >
          {{ $t(`code.newsletter_campaign_status.${item.status}`) }}
        </v-chip>
      </template>

      <template #item.actions="{item}">
        <v-tooltip
          v-if="!item.isArchived && item.canArchive"
          top
        >
          <template #activator="{on}">
            <v-btn
              icon
              x-small
              v-on="on"
              @click.stop="archiveCampaign(item)"
            >
              <v-icon small>
                mdi-archive-plus
              </v-icon>
            </v-btn>
          </template>
          {{ $t('button.move_to_archive') }}
        </v-tooltip>
        <v-tooltip
          v-else-if="item.isArchived && item.canArchive"
          top
        >
          <template #activator="{on}">
            <v-btn
              icon
              x-small
              v-on="on"
              @click.stop="archiveCampaign(item)"
            >
              <v-icon small>
                mdi-archive-minus
              </v-icon>
            </v-btn>
          </template>
          {{ $t('button.unarchive') }}
        </v-tooltip>
        <v-tooltip
          v-if="item.canDelete"
          top
        >
          <template #activator="{on}">
            <v-btn
              icon
              x-small
              v-on="on"
              @click.stop="deleteCampaign(item)"
            >
              <v-icon small>
                mdi-delete
              </v-icon>
            </v-btn>
          </template>
          {{ $t('button.delete') }}
        </v-tooltip>
      </template>
    </v-data-table>
  </toolbar-container>
</template>

<script lang="ts" setup>

import {
  ref, computed, onBeforeUnmount, getCurrentInstance, onMounted,
} from 'vue';
import { DateRange } from '@/services/date-range';

import i18n from '@/plugins/i18n';
import store from '@/store/store';
import localized from '@/plugins/vue-localized-formatter/src/localized';

import MainToolbar from '@/components/views/main/MainToolbar.vue';
import DateRangePickerField from '@/components/pickers/DateRangePickerField.vue';
import { loadStoreModule, tryUnloadStoreModule, unloadStoreModule } from '@/services/module-loader';
import { performDispatchAction, performSaveAction } from '@/services/vue-utils';
import { useRouter } from 'vue-router/composables';
import CampaignManagerStore from '@/store/marketing-campaign-manager-store';
import MarketingCampaign, { MarketingCampaignStatus } from '@/grpc-api/model/marketing-campaign';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import { cloneModel } from '@/model/model-utils';
import RepeatableTimer from '@/services/repeatable-timer';
import PaywallUpgradeDialog from '@/components/dialogs/PaywallUpgradeDialog.vue';
import PaywallButton from '@/components/buttons/PaywallButton.vue';
import NewsletterCampaignDetailDialog from './NewsletterCampaignDetailsDialog.vue';
import NewsletterQuotaBar from './NewsletterQuotaBar.vue';
import { colorForCampaignStatus } from './newsletter';

const headers = [
  {
    text: i18n.t('label.date_created'), align: 'start', width: '160px', value: 'date',
  },
  {
    text: i18n.t('label.status'), align: 'center', value: 'status', width: '160px',
  },
  {
    text: i18n.t('label.newsletter_name'), align: 'start', value: 'title',
  },
  {
    text: i18n.t('label.recipient_count'), align: 'center', value: 'recipientCount', width: '200px',
  },
  {
    text: i18n.t('label.language'), align: 'center', value: 'locale', width: '200px',
  },
  {
    text: '', value: 'actions', width: '64px', align: 'center', sortable: false,
  },
];
const isNewsletterFeatureAllowed = computed<boolean>(() => store.getters.isNewsletterFeatureAllowed);

const router = useRouter();

const selectedMarketingCampaign = ref(undefined as undefined | MarketingCampaign);

// actions
const newCampaign = () => {
  router.push('/newsletter/new');
};

const showCampaignDetail = async (campaign: MarketingCampaign) => {
  if (campaign.status === MarketingCampaignStatus.Draft) {
    router.push(`/newsletter/${campaign.id}`);
  } else {
    const campaignToSelect = await performDispatchAction<MarketingCampaign>('getMarketingCampaign', { campaign });
    if (!campaignToSelect) return;

    selectedMarketingCampaign.value = campaignToSelect;
  }
};

// campaigns
const campaigns = computed(() => store.getters.marketingCampaignManagerCampaigns as MarketingCampaign[]);

// filter show archived
const showArchived = computed(() => store.getters.marketingCampaignManagerFilter.showArchived as Boolean);

const changeShowArchived = async (value: boolean) => {
  performDispatchAction('updateMarketingCampaignFilter', { showArchived: value });
};

// filter date range
const filterDateRange = computed(() => store.getters.marketingCampaignManagerFilter.dateRange as DateRange);

const filterDateRangeSelected = async (dateRange: DateRange) => {
  performDispatchAction('updateMarketingCampaignFilter', { dateRange });
};

// load campaign manager
const load = async () => {
  if (store.getters.isMarketingCampaignManagerLoaded) return;
  await performDispatchAction('loadMarketingCampaignManager', {});
};

const reset = async () => {
  store.dispatch('resetMarketingCampaignManager');
  tryUnloadStoreModule('serviceManager', async () => store.dispatch('resetServiceManager'));
};

// campaign actions
const archiveCampaign = async (campaign: MarketingCampaign) => {
  const action = async () => {
    const archivedCampaign = cloneModel(campaign);
    archivedCampaign.isArchived = !archivedCampaign.isArchived;
    await performSaveAction(
      undefined,
      async () => store.dispatch('archiveMarketingCampaign', { campaign: archivedCampaign }),
      { hideSuccess: true },
    );
  };

  showConfirmationDialog(
    campaign.title ?? i18n.t('title.newsletter_campaigns'),
    i18n.t('message.confirm_archive_campaign'),
    { action },
    {},
  );
};

const deleteCampaign = async (campaign: MarketingCampaign) => {
  const action = async () => {
    await performSaveAction(
      undefined,
      async () => store.dispatch('deleteMarketingCampaign', { campaign }),
      { hideSuccess: true },
    );
  };

  showConfirmationDialog(
    campaign.title ?? i18n.t('title.newsletter_campaigns'),
    i18n.t('message.confirm_delete_campaign_draft'),
    { action },
    {},
  );
};

let refreshTimer = undefined as RepeatableTimer | undefined;
const refresh = async () => {
  try {
    await store.dispatch('refreshMarketingCampaigns');
  } catch (e) {
    console.log('refresh campaigns error: ', e);
  }
};

onMounted(() => {
  // load store module
  loadStoreModule(
    getCurrentInstance()?.proxy!,
    'marketingCampaignManager',
    CampaignManagerStore,
    async () => { load(); },
  );

  // start refresh timer
  refreshTimer = new RepeatableTimer(refresh);
  refreshTimer.start(5000);
});

onBeforeUnmount(() => {
  refreshTimer?.stop();
  unloadStoreModule(getCurrentInstance()?.proxy!, 'marketingCampaignManager', async () => { reset(); });
});

</script>
